/**
 * Turntable assets — where the 360° frames live, how they load, and how they
 * take a client's livery.
 *
 * `npm run oem:extract` writes the demo's inline `const TURN` (11.7 MB of
 * base64 WebP) to `public/turntable/<MODEL>/` as static files: 24 frames
 * (`f00–f23`), 24 ID masks (`m00–m23`), 6 exploded frames and masks
 * (`x0–x5`, `xm0–xm5`), a card icon and a `manifest.json`. Nothing here is in
 * the bundle; a viewer fetches one model's files when it mounts.
 *
 * ## Livery
 *
 * The frames are studio renders of **Meridian** machines — pink paint. Under
 * the Komatsu brand the paint is recoloured to Komatsu yellow in a canvas:
 * every pixel whose hue sits in the pink band keeps its lightness and alpha
 * and takes the livery hue and saturation, so the render's shading survives.
 * Steel, tyres, glass, the red fire bottles and the yellow handrails sit
 * outside the band and are untouched. The ID masks are separate images and
 * are never recoloured, so the highlight glow stays exact.
 *
 * A CSS `filter` cannot do this: the closest `hue-rotate`/`saturate` lands
 * the pink on orange, turns the red fire bottles olive, and reaching yellow
 * needs `brightness(1.95)`, which washes the steel and tyres out to white.
 * {@link LIVERY_CSS_FALLBACK} keeps that approximation only for a browser
 * that cannot read canvas pixels.
 *
 * Battery and autonomous machines keep the demo's own drive tints
 * (`.drv-batt` / `.drv-auto`, demo lines 555–556) under every brand — the
 * tint carries meaning independent of the client, as the card edge colours
 * in `MachineCard` do.
 *
 * @see scripts/oem/extract-turntable.mjs
 * @see src/versions/v1/shared/viz/Turntable.tsx
 */

import { useEffect, useState } from 'react';
import type { BrandId } from '../BrandContext';
import { sideViewModel, type Machine, type ModelCode } from '../../schema/machine';
import type { FrameSet } from './Turntable.transform';

/** The nine models the demo rendered. `ML-14` and `MT-50` alias onto two of them. */
export const TURNTABLE_MODELS: ReadonlySet<ModelCode> = new Set<ModelCode>([
  'MJ-2D',
  'ML-21',
  'ML-21E',
  'ML-21R',
  'MP-60',
  'MP-60A',
  'MS-45',
  'MS-120',
  'MT-63',
]);

/** The rendered model for a fleet model, following the demo's `TURN_MAP`, or null. */
export function turntableModel(model: ModelCode): ModelCode | null {
  const m = sideViewModel(model);
  return TURNTABLE_MODELS.has(m) ? m : null;
}

/** True when the model (or its alias) has 360° frames. */
export const hasTurntable = (model: ModelCode): boolean => turntableModel(model) !== null;

/** Root of the extracted assets. Absolute from the site root, like `/data/oem`. */
const ROOT = '/turntable';

const pad2 = (i: number) => String(i).padStart(2, '0');

/** Frame, mask, exploded frame or exploded mask `i` of a rendered model. */
export function turntableFrameUrl(
  model: ModelCode,
  kind: 'f' | 'm' | 'x' | 'xm',
  i: number,
): string {
  const stem = kind === 'f' || kind === 'm' ? `${kind}${pad2(i)}` : `${kind}${i}`;
  return `${ROOT}/${model}/${stem}.webp`;
}

/** The card thumbnail of a rendered model. */
export const turntableIconUrl = (model: ModelCode): string => `${ROOT}/${model}/icon.webp`;

/** `public/turntable/<MODEL>/manifest.json`, as the extractor writes it. */
export interface TurntableManifest extends FrameSet {
  model: ModelCode;
  /** Frame size, px. Anchors are fractions of these. */
  w: number;
  h: number;
  /** Component → mask index. The mask's red channel is `(index + 1) × 16`. */
  compidx: Record<string, number>;
  icon: boolean;
  explode: FrameSet | null;
}

const manifests = new Map<ModelCode, Promise<TurntableManifest>>();

function loadManifest(model: ModelCode): Promise<TurntableManifest> {
  let p = manifests.get(model);
  if (!p) {
    p = fetch(`${ROOT}/${model}/manifest.json`).then((r) => {
      if (!r.ok) throw new Error(`turntable manifest ${model}: HTTP ${r.status}`);
      return r.json() as Promise<TurntableManifest>;
    });
    // A failed fetch is not cached, so a later mount can retry.
    p.catch(() => manifests.delete(model));
    manifests.set(model, p);
  }
  return p;
}

/** The manifest for a rendered model; `null` until loaded, on error, or with no model. */
export function useTurntableManifest(model: ModelCode | null): {
  manifest: TurntableManifest | null;
  error: boolean;
} {
  const [state, setState] = useState<{
    model: ModelCode | null;
    manifest: TurntableManifest | null;
    error: boolean;
  }>({ model: null, manifest: null, error: false });
  useEffect(() => {
    if (!model) return;
    let live = true;
    loadManifest(model).then(
      (manifest) => live && setState({ model, manifest, error: false }),
      () => live && setState({ model, manifest: null, error: true }),
    );
    return () => {
      live = false;
    };
  }, [model]);
  return state.model === model
    ? { manifest: state.manifest, error: state.error }
    : { manifest: null, error: false };
}

// ---------------------------------------------------------------------------
// Livery
// ---------------------------------------------------------------------------

/** The demo's `drvOf` (line 2017): battery, then autonomous, then diesel. */
export type DriveKind = 'diesel' | 'batt' | 'auto';

export const driveKind = (f: Pick<Machine, 'battery' | 'autonomous'>): DriveKind =>
  f.battery ? 'batt' : f.autonomous ? 'auto' : 'diesel';

/** Demo lines 555–556. The drive tints applied to every turntable image. */
const DRIVE_FILTER: Readonly<Record<DriveKind, string | undefined>> = {
  diesel: undefined,
  batt: 'hue-rotate(122deg) saturate(1.12)',
  auto: 'hue-rotate(-132deg) saturate(.92) brightness(1.04)',
};

/** Target paint per brand, as HSL hue (degrees) and saturation (0–1). `null` keeps the render. */
const LIVERY_PAINT: Readonly<Record<BrandId, { hue: number; sat: number } | null>> = {
  // Komatsu yellow #FFCC00 is hsl(48, 100%, 50%).
  komatsu: { hue: 48, sat: 1 },
  meridian: null,
};

/**
 * The CSS-filter approximation of the Komatsu livery, used only when canvas
 * pixels cannot be read. Pink lands on a deep orange; see the module note.
 */
export const LIVERY_CSS_FALLBACK: Readonly<Partial<Record<BrandId, string>>> = {
  komatsu: 'hue-rotate(70deg) saturate(1.6) brightness(1.08)',
};

/** How one machine's turntable images are painted under one brand. */
export interface LiveryPlan {
  /** Recolour the pink paint to this hue/saturation in a canvas. */
  paint: { hue: number; sat: number } | null;
  /** CSS filter on the frame `<img>` (drive tint), if any. */
  filter: string | undefined;
}

export function liveryPlan(brandId: BrandId, drive: DriveKind): LiveryPlan {
  if (drive !== 'diesel') return { paint: null, filter: DRIVE_FILTER[drive] };
  return { paint: LIVERY_PAINT[brandId], filter: undefined };
}

/** Pink paint band in HSL: hue 295–352°, saturation ≥ 0.28, not near-black or near-white. */
function isPaint(h: number, s: number, l: number): boolean {
  return h >= 295 && h <= 352 && s >= 0.28 && l > 0.08 && l < 0.94;
}

/** Replace the hue of every paint pixel in place, keeping lightness and alpha. */
function repaint(px: Uint8ClampedArray, hue: number, sat: number): void {
  const hk = hue / 360;
  for (let p = 0; p < px.length; p += 4) {
    if (px[p + 3] === 0) continue;
    const r = px[p] / 255;
    const g = px[p + 1] / 255;
    const b = px[p + 2] / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    const d = max - min;
    if (d === 0) continue;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h: number;
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
    else if (max === g) h = ((b - r) / d + 2) * 60;
    else h = ((r - g) / d + 4) * 60;
    if (!isPaint(h, s, l)) continue;
    // hsl → rgb with the livery hue, full livery saturation scaled by how
    // saturated the source pixel was (so soft reflections stay soft).
    const s2 = Math.min(1, sat * Math.min(1, s / 0.6));
    const q = l < 0.5 ? l * (1 + s2) : l + s2 - l * s2;
    const pp = 2 * l - q;
    const ch = (t: number) => {
      const tt = t < 0 ? t + 1 : t > 1 ? t - 1 : t;
      if (tt < 1 / 6) return pp + (q - pp) * 6 * tt;
      if (tt < 1 / 2) return q;
      if (tt < 2 / 3) return pp + (q - pp) * (2 / 3 - tt) * 6;
      return pp;
    };
    px[p] = Math.round(ch(hk + 1 / 3) * 255);
    px[p + 1] = Math.round(ch(hk) * 255);
    px[p + 2] = Math.round(ch(hk - 1 / 3) * 255);
  }
}

const repainted = new Map<string, Promise<string>>();

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const im = new Image();
    im.decoding = 'async';
    im.addEventListener('load', () => resolve(im), { once: true });
    im.addEventListener('error', () => reject(new Error(`image ${src} failed to load`)), {
      once: true,
    });
    im.src = src;
  });
}

/**
 * The URL of `src` recoloured to a livery — an object URL, cached per source
 * and paint for the session. Rejects when the canvas cannot be read; callers
 * fall back to {@link LIVERY_CSS_FALLBACK}.
 */
export function repaintedUrl(src: string, paint: { hue: number; sat: number }): Promise<string> {
  const key = `${paint.hue}/${paint.sat}|${src}`;
  let p = repainted.get(key);
  if (!p) {
    p = loadImage(src).then(
      (im) =>
        new Promise<string>((resolve, reject) => {
          const cv = document.createElement('canvas');
          cv.width = im.naturalWidth;
          cv.height = im.naturalHeight;
          const ctx = cv.getContext('2d', { willReadFrequently: true });
          if (!ctx) return reject(new Error('2D canvas unavailable'));
          ctx.drawImage(im, 0, 0);
          const data = ctx.getImageData(0, 0, cv.width, cv.height);
          repaint(data.data, paint.hue, paint.sat);
          ctx.putImageData(data, 0, 0);
          cv.toBlob(
            (blob) =>
              blob ? resolve(URL.createObjectURL(blob)) : reject(new Error('toBlob failed')),
            'image/webp',
            0.9,
          );
        }),
    );
    p.catch(() => repainted.delete(key));
    repainted.set(key, p);
  }
  return p;
}

/**
 * Resolve a list of image URLs through a livery plan. Returns the source
 * URLs unchanged when the plan has no paint; otherwise the repainted URLs
 * once every one is ready (`null` meanwhile), or the sources plus the CSS
 * fallback filter if repainting fails.
 */
export function useLiveryUrls(
  srcs: readonly string[] | null,
  plan: LiveryPlan,
  brandId: BrandId,
): { urls: readonly string[] | null; filter: string | undefined } {
  const paint = plan.paint;
  const key = srcs ? srcs.join('|') : '';
  const [state, setState] = useState<{
    key: string;
    urls: readonly string[] | null;
    failed: boolean;
  }>({
    key: '',
    urls: null,
    failed: false,
  });
  const stateKey = `${paint ? `${paint.hue}/${paint.sat}` : 'none'}#${key}`;
  useEffect(() => {
    if (!srcs || !paint) return;
    let live = true;
    Promise.all(srcs.map((s) => repaintedUrl(s, paint))).then(
      (urls) => live && setState({ key: stateKey, urls, failed: false }),
      () => live && setState({ key: stateKey, urls: srcs, failed: true }),
    );
    return () => {
      live = false;
    };
    // `stateKey` covers `srcs` and `paint` by value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateKey]);

  if (!srcs) return { urls: null, filter: plan.filter };
  if (!paint) return { urls: srcs, filter: plan.filter };
  if (state.key !== stateKey) return { urls: null, filter: plan.filter };
  return { urls: state.urls, filter: state.failed ? LIVERY_CSS_FALLBACK[brandId] : plan.filter };
}
